(() => {
  'use strict';
  if (window.UniOrderGift) return;
  let cfg = {};
  try {
    const parsed = JSON.parse(document.querySelector('#UniMobileConfig')?.textContent || '{}');
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) cfg = parsed;
  } catch {}
  const root = window.Shopify?.routes?.root || '/';
  const giftId = String(cfg.giftVariantId || '');
  const messages = cfg.strings || {};
  const errorMessage = messages.error || 'We could not add your free sticker. You can still checkout with your other items.';
  const unavailableMessage = messages.unavailable || 'The free sticker is currently unavailable. You can still checkout with your other items.';
  const preferences = new Map();
  const unavailableStorage = new Set();
  const freshCarts = new WeakSet();
  let queue = Promise.resolve();
  const isGift = item => Boolean(giftId) && String(item.variant_id || item.id) === giftId;
  const hasPaidItem = cart => cart.items.some(item => !isGift(item) && item.quantity > 0 && Number(item.final_line_price) > 0);
  const isFree = item => Number(item.price) === 0 && Number(item.final_line_price) === 0 &&
    ['original_price', 'final_price', 'original_line_price'].every(field => item[field] === undefined || Number(item[field]) === 0);

  function preferenceKey(cart) {
    if (!cart.token) return null;
    const token = String(cart.token);
    let left = 2166136261;
    let right = 5381;
    for (let index = 0; index < token.length; index++) {
      left = Math.imul(left ^ token.charCodeAt(index), 16777619);
      right = Math.imul(right, 33) ^ token.charCodeAt(index);
    }
    return 'uni-order-gift:v1:' + (left >>> 0).toString(16) + ':' + (right >>> 0).toString(16);
  }

  function declined(cart) {
    const key = preferenceKey(cart);
    if (!key) return false;
    for (const name of ['localStorage', 'sessionStorage']) {
      if (unavailableStorage.has(name)) continue;
      try {
        const storage = window[name];
        if (storage) return storage.getItem(key) === '1';
      } catch { unavailableStorage.add(name); }
    }
    return preferences.get(key) === true;
  }

  function remember(cart, value) {
    const key = preferenceKey(cart);
    if (!key) return;
    if (value) preferences.set(key, true);
    else preferences.delete(key);
    for (const name of ['localStorage', 'sessionStorage']) {
      if (unavailableStorage.has(name)) continue;
      try {
        if (value) window[name]?.setItem(key, '1');
        else window[name]?.removeItem(key);
      } catch { unavailableStorage.add(name); }
    }
  }

  function readCart(data) {
    if (!data || !Array.isArray(data.items)) throw new Error('Unable to read your cart. Please try again.');
    if (!data.items.length) remember(data, false);
    return data;
  }

  async function api(path, body) {
    const response = await fetch(root + path, {
      credentials: 'same-origin', cache: 'no-store', method: body === undefined ? 'GET' : 'POST',
      headers: { Accept: 'application/json', ...(body === undefined ? {} : { 'Content-Type': 'application/json' }) },
      ...(body === undefined ? {} : { body: JSON.stringify(body) })
    });
    let data;
    try {
      data = await response.json();
    } catch {
      const error = new Error('Unable to read the cart response. Please check your cart.');
      error.requestRejected = response.status >= 400 && response.status < 500 && response.status !== 422;
      throw error;
    }
    if (!data || typeof data !== 'object' || !response.ok || Number(data.status) >= 400) {
      const error = new Error(typeof data?.description === 'string' ? data.description : typeof data?.message === 'string' ? data.message : 'Unable to update your cart. Please check your cart.');
      const status = response.ok ? Number(data?.status) : response.status;
      error.requestRejected = status >= 400 && status < 500 && status !== 422;
      throw error;
    }
    return data;
  }

  const currentCart = async () => {
    const cart = readCart(await api('cart.js'));
    freshCarts.add(cart);
    return cart;
  };
  const giftLines = cart => cart.items.filter(isGift);

  async function normalize(cart, allow) {
    const limit = giftLines(cart).length + 3;
    for (let step = 0; step < limit; step++) {
      const lines = giftLines(cart);
      allow = allow && hasPaidItem(cart) && !declined(cart);
      if (lines.some(item => !isFree(item))) {
        allow = false;
        if (hasPaidItem(cart) && !declined(cart)) service.lastError = unavailableMessage;
      }
      const target = allow ? (lines.length > 1 ? lines[lines.length - 1] : lines[0]) : lines[0];
      if (!target) return cart;
      const quantity = allow && lines.length === 1 ? 1 : 0;
      const legacy = quantity === 1 && (target.properties?._uni_offer === 'first-website-order' || target.properties?.Offer === 'First-order gift');
      if (target.quantity === quantity && !legacy) return cart;
      const body = { id: target.key, quantity };
      if (legacy) {
        body.properties = { ...target.properties, _uni_offer: 'every-order' };
        if (body.properties.Offer === 'First-order gift') delete body.properties.Offer;
      }
      cart = readCart(await api('cart/change.js', body));
    }
    throw new Error('Unable to finish updating your cart. Please try again.');
  }

  async function reconcile(cart) {
    service.lastError = '';
    if (cfg.autoGift !== true || !giftId) return cart;
    const paid = hasPaidItem(cart);
    const optedOut = declined(cart);
    const available = cfg.giftAvailable === true && cfg.giftPrice !== null && cfg.giftPrice !== undefined && Number(cfg.giftPrice) === 0;
    const pricedGift = giftLines(cart).some(item => !isFree(item));
    const allow = paid && !optedOut && available && !pricedGift;
    if (paid && !optedOut && (!available || pricedGift)) service.lastError = unavailableMessage;
    cart = await normalize(cart, allow);
    if (!allow || service.lastError || giftLines(cart).length || !hasPaidItem(cart)) return cart;
    let additionFailed = false;
    try {
      await api('cart/add.js', { items: [{ id: giftId, quantity: 1, properties: { _uni_offer: 'every-order' } }] });
    } catch {
      additionFailed = true;
    }
    cart = await currentCart();
    const nowPriced = giftLines(cart).some(item => !isFree(item));
    const stillAllowed = hasPaidItem(cart) && !declined(cart) && !nowPriced;
    if (nowPriced) service.lastError = unavailableMessage;
    cart = await normalize(cart, stillAllowed);
    if (stillAllowed && giftLines(cart).length === 1) service.lastError = '';
    else if (!service.lastError && (additionFailed || (stillAllowed && !giftLines(cart).length))) service.lastError = errorMessage;
    return cart;
  }

  function enqueue(operation) {
    const run = () => window.navigator?.locks?.request
      ? window.navigator.locks.request('uni-order-gift-cart-v1', operation)
      : operation();
    const result = queue.catch(() => {}).then(run);
    queue = result;
    return result;
  }

  const service = {
    isGift,
    lastError: '',
    sync() {
      return enqueue(async () => {
        const cart = await reconcile(await currentCart());
        // Gift cleanup can return a mutation response whose total includes checkout shipping.
        // Reuse a final GET when reconciliation already read it; otherwise verify before display.
        return freshCarts.has(cart) ? cart : currentCart();
      });
    },
    add(items) {
      const copiedItems = items.map(item => ({ ...item, ...(item.properties ? { properties: { ...item.properties } } : {}) }));
      return enqueue(async () => {
        await currentCart();
        try {
          await api('cart/add.js', { items: copiedItems });
        } catch (cause) {
          if (cause?.requestRejected === true) throw cause;
          const error = new Error(messages.addUncertain || 'We could not confirm whether your items were added. View your cart before trying again.');
          error.addUncertain = true;
          error.cause = cause;
          throw error;
        }
        try {
          return await reconcile(await currentCart());
        } catch (cause) {
          const error = new Error(messages.addedRefreshError || 'Your items were added to the cart, but we could not refresh it. View your cart before adding again.');
          error.added = true;
          error.cause = cause;
          throw error;
        }
      });
    },
    change(key, quantity, options = {}) {
      const declineGift = options.declineGift === true;
      return enqueue(async () => {
        if (!Number.isInteger(quantity) || quantity < 0) throw new Error('Please enter a valid quantity.');
        // Only explicit gift removal needs a preflight to verify the line before recording refusal.
        const before = declineGift ? await currentCart() : null;
        const line = before?.items.find(item => item.key === String(key));
        if (declineGift && quantity === 0 && line && isGift(line)) remember(before, true);
        let after;
        try {
          after = readCart(await api('cart/change.js', { id: String(key), quantity }));
        } catch (error) {
          // A stale line can fail after another tab emptied the cart. Still observe that empty
          // cart to clear its old refusal, and preserve the original failure for UI recovery.
          if (!declineGift) { try { await currentCart(); } catch {} }
          throw error;
        }
        if (declineGift && quantity === 0 && line && isGift(line)) remember(after, after.items.length > 0);
        return reconcile(after);
      });
    }
  };
  window.UniOrderGift = service;
})();

(() => {
  'use strict';
  document.addEventListener('submit', async (event) => {
    let form = event.target;
    let button = event.submitter;
    if (!(form instanceof HTMLFormElement) || button?.name !== 'checkout' || !window.UniOrderGift) return;
    const container = form.closest('[data-uni-cart-page], [data-dialog-body]') || form;
    if (form.dataset.uniGiftChecked === 'true') {
      delete form.dataset.uniGiftChecked;
      return;
    }
    event.preventDefault();
    event.stopImmediatePropagation();
    if (container.dataset.uniCartUncertain === 'true' || container.dataset.uniCheckoutPreparing === 'true' || container.dataset.uniCheckoutBusy === 'true' || form.dataset.uniGiftBusy === 'true') return;
    const quantities = [...container.querySelectorAll('[data-cart-quantity]')];
    const invalidQuantity = quantities.find(input => !input.checkValidity());
    if (invalidQuantity) {
      invalidQuantity.reportValidity();
      return;
    }
    const originalForm = form;
    const originalButton = button;
    const originalLabel = button.dataset.uniCheckoutLabel || button.textContent;
    const originalDisabled = button.disabled;
    let controls = [];
    let checkoutLabel = originalLabel;
    const strings = {
      checkoutError: 'Unable to check your cart. Please try again.',
      continueWithout: 'Continue without the free gift',
      updating: 'Updating…',
      openingCheckout: 'Opening checkout…'
    };
    try {
      const config = JSON.parse(document.getElementById('UniMobileConfig')?.textContent || '{}');
      for (const key of Object.keys(strings)) {
        const value = config?.purchaseStrings?.[key] || config?.strings?.[key];
        if (typeof value === 'string' && value) strings[key] = value;
      }
    } catch {}
    container.dataset.uniCheckoutPreparing = 'true';
    form.dataset.uniGiftBusy = 'true';
    form.setAttribute('aria-busy', 'true');
    button.disabled = true;
    button.textContent = container.dataset.uniCartBusy === 'true' || quantities.some(input => input.value !== input.defaultValue) ? strings.updating : strings.openingCheckout;
    try {
      if (typeof container.uniPrepareCheckout === 'function') {
        if (await container.uniPrepareCheckout() !== true) return;
      } else {
        if (container.dataset.uniCartBusy === 'true') return;
        const changedQuantity = quantities.find(input => input.value !== input.defaultValue);
        if (changedQuantity) {
          changedQuantity.dispatchEvent(new Event('change', { bubbles: true }));
          return;
        }
      }
      // Saving a quantity replaces the cart form. Continue with the confirmed form.
      if (!container.isConnected) return;
      button = container.querySelector('[name="checkout"]');
      if (!button || !button.form?.isConnected) return;
      form = button.form;
      checkoutLabel = button.dataset.uniCheckoutLabel || (button === originalButton ? originalLabel : button.textContent);
      delete container.dataset.uniCheckoutPreparing;
      container.dataset.uniCheckoutBusy = 'true';
      form.dataset.uniGiftBusy = 'true';
      form.setAttribute('aria-busy', 'true');
      controls = [...container.querySelectorAll('button, input')].map(node => [node, node === button ? false : node.disabled]);
      controls.forEach(([node]) => { node.disabled = true; });
      button.textContent = strings.openingCheckout;
      const cart = await window.UniOrderGift.sync();
      if (!cart.item_count) {
        delete container.dataset.uniCheckoutBusy;
        window.UniMobile?.renderCart(container, cart);
        return;
      }
      if (window.UniOrderGift.lastError && form.dataset.uniGiftProceed !== 'true') {
        form.querySelector('[data-gift-check-error]')?.remove();
        const message = document.createElement('div');
        message.dataset.giftCheckError = '';
        message.className = 'uni-gift-error';
        message.setAttribute('role', 'alert');
        const text = document.createElement('p');
        text.textContent = window.UniOrderGift.lastError;
        const proceed = document.createElement('button');
        proceed.type = 'button';
        proceed.className = 'button button--secondary';
        proceed.textContent = strings.continueWithout;
        proceed.addEventListener('click', () => {
          form.dataset.uniGiftProceed = 'true';
          form.requestSubmit(button);
        });
        message.append(text, proceed);
        form.append(message);
        return;
      }
      if (!form.isConnected) return;
      form.dataset.uniGiftChecked = 'true';
      delete form.dataset.uniGiftProceed;
      controls.forEach(([node, disabled]) => { node.disabled = disabled; });
      button.disabled = false;
      form.requestSubmit(button);
    } catch (error) {
      let message = form.querySelector('[data-gift-check-error]');
      if (!message) {
        message = document.createElement('p');
        message.dataset.giftCheckError = '';
        message.className = 'uni-error';
        message.setAttribute('role', 'alert');
        form.append(message);
      }
      message.textContent = strings.checkoutError;
    } finally {
      delete originalForm.dataset.uniGiftBusy;
      originalForm.removeAttribute('aria-busy');
      delete form.dataset.uniGiftBusy;
      form.removeAttribute('aria-busy');
      delete container.dataset.uniCheckoutPreparing;
      delete container.dataset.uniCheckoutBusy;
      controls.forEach(([node, disabled]) => { node.disabled = disabled; });
      originalButton.textContent = originalLabel;
      originalButton.disabled = originalDisabled || container.dataset.uniCartUncertain === 'true';
      const activeButton = container.querySelector('[name="checkout"]');
      if (activeButton && activeButton !== originalButton) {
        activeButton.textContent = activeButton === button ? checkoutLabel : activeButton.dataset.uniCheckoutLabel || activeButton.textContent;
        activeButton.disabled = container.dataset.uniCartUncertain === 'true';
      }
    }
  }, true);
})();
